/* eslint-disable react/require-default-props */
import React from 'react';
import {View} from 'react-native';

import FormattedNumber from '/component/base/FormattedNumber';
import ListItem from '/component/base/ListItem';
import Text from '/component/base/Text';
import Gutter from '/component/layout/Gutter';
import ScreenSection from '/component/layout/ScreenSection';
import {OrderDetails} from '/constant/TestIDs';
import {CURRENCY_USD} from '/enum/currency.enum';
import {withTranslation} from '/provider/TranslationProvider';
import {generateComponentCallback} from '/util/component.util';

import {styles as sectionStyles} from '../Section.style';

import {styles} from './SummarySection.style';

const normalizeAmount = (value: number) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const getCurrencyAccessibilityValue = (value: number, currencyCode: string) =>
  `${value} ${currencyCode}`;

interface SummarySectionProps {
  tt?: (key: any, value: any) => any;
  currencyCode?: string;
  subtotal?: number;
  shipping?: number;
  tax?: number;
  total?: number;
}

export const SummarySection: React.FC<SummarySectionProps> = ({
  tt = withTranslation.ttDefaultProp,
  currencyCode = '',
  subtotal = 0,
  shipping = 0,
  tax = 0,
  total = 0,
}: SummarySectionProps) => {
  const summaryCurrencyCode = currencyCode || CURRENCY_USD;
  const subtotalDisplayValue = normalizeAmount(subtotal);
  const shippingDisplayValue = normalizeAmount(shipping);
  const taxDisplayValue = normalizeAmount(tax);
  const totalDisplayValue = normalizeAmount(total);

  return (
    <ScreenSection
      style={{...sectionStyles, content: styles.base}}
      title={generateComponentCallback({
        component: (
          <Gutter>
            <Text
              variant="subheading"
              emphasis="low"
              testID={OrderDetails.summarySectionHeading}
              accessible={true}
              accessibilityRole="header">
              {tt('heading', 'Order Summary')}
            </Text>
          </Gutter>
        ),
      })}>
      <Gutter>
        <ListItem
          testID={OrderDetails.summarySubtotal}
          readonly
          style={{base: styles.line}}
          accessible={false}
          accessibilityLabel={`${tt(
            'orderSubtotal.label',
            'Subtotal',
          )}: ${getCurrencyAccessibilityValue(
            subtotalDisplayValue,
            summaryCurrencyCode,
          )}`}
          label={
            <Text testID={OrderDetails.summarySubtotalLabel} accessible={true}>
              {tt('orderSubtotal.label', 'Subtotal')}
            </Text>
          }
          suffix={
            <FormattedNumber
              testID={OrderDetails.summarySubtotalValue}
              value={subtotalDisplayValue}
              currencyCode={summaryCurrencyCode}
              separationStyle="thousand"
              decimalPlaces={2}
              accessible={true}
            />
          }
        />
        <ListItem
          testID={OrderDetails.summaryShipping}
          readonly
          style={{base: styles.line}}
          accessible={false}
          accessibilityLabel={`${tt(
            'orderShipping.label',
            'Shipping',
          )}: ${getCurrencyAccessibilityValue(
            shippingDisplayValue,
            summaryCurrencyCode,
          )}`}
          label={
            <Text testID={OrderDetails.summaryShippingLabel} accessible={true}>
              {tt('orderShipping.label', 'Shipping')}
            </Text>
          }
          suffix={
            <FormattedNumber
              testID={OrderDetails.summaryShippingValue}
              value={shippingDisplayValue}
              currencyCode={summaryCurrencyCode}
              separationStyle="thousand"
              decimalPlaces={2}
              accessible={true}
            />
          }
        />
        <ListItem
          testID={OrderDetails.summaryTax}
          readonly
          style={{base: styles.line}}
          accessible={false}
          accessibilityLabel={`${tt(
            'orderTax.label',
            'Tax',
          )}: ${getCurrencyAccessibilityValue(
            taxDisplayValue,
            summaryCurrencyCode,
          )}`}
          label={
            <Text testID={OrderDetails.summaryTaxLabel} accessible={true}>
              {tt('orderTax.label', 'Tax')}
            </Text>
          }
          suffix={
            <FormattedNumber
              testID={OrderDetails.summaryTaxValue}
              value={taxDisplayValue}
              currencyCode={summaryCurrencyCode}
              separationStyle="thousand"
              decimalPlaces={2}
              accessible={true}
            />
          }
        />
        <View style={styles.divider} />
        <ListItem
          testID={OrderDetails.summaryTotal}
          readonly
          style={{base: styles.line}}
          accessible={false}
          accessibilityLabel={`${tt(
            'orderTotal.label',
            'Total',
          )}: ${getCurrencyAccessibilityValue(
            totalDisplayValue,
            summaryCurrencyCode,
          )}`}
          label={
            <Text testID={OrderDetails.summaryTotalLabel} accessible={true}>
              {tt('orderTotal.label', 'Total')}
            </Text>
          }
          suffix={
            <FormattedNumber
              testID={OrderDetails.summaryTotalValue}
              value={totalDisplayValue}
              currencyCode={summaryCurrencyCode}
              separationStyle="thousand"
              decimalPlaces={2}
              accessible={true}
            />
          }
        />
      </Gutter>
    </ScreenSection>
  );
};

export default withTranslation('screen.orderDetail.section.summary')(
  SummarySection,
);
